import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import Header from '../components/Header';
import { toast } from 'sonner';
import { saveDesignDraft, getDesignDraft, submitDesignDetails, uploadInspirationImage, listUploads, deleteUpload } from '../lib/api';
import { OrderInfoSection } from '../components/design-details/OrderInfoSection';
import { EventDetailsSection } from '../components/design-details/EventDetailsSection';
import { WordingSection } from '../components/design-details/WordingSection';
import { StyleSection } from '../components/design-details/StyleSection';
import { BundleItemsSection } from '../components/design-details/BundleItemsSection';
import { InspirationUploads } from '../components/design-details/InspirationUploads';
import { PrintDetailsSection } from '../components/design-details/PrintDetailsSection';
import { CopyrightSection, FinalNotesSection } from '../components/design-details/FinalSections';

const INITIAL_FORM = {
  orderNumber: '',
  fullName: '',
  email: '',
  eventType: '',
  eventDate: '',
  orderType: 'digital',
  exactNames: '',
  exactPhrasing: '',
  specialDetails: '',
  theme: '',
  colors: '',
  style: 'trust-creative',
  specificElements: '',
  bundleItems: [],
  favorItemChoice: '',
  quantityRange: '',
  inspirationFileIds: [],
  shippingAddress: '',
  copyrightedCharacters: 'no',
  additionalNotes: '',
  agreedToTerms: false,
  agreedToPricingAdjustment: false,
};

const FAVOR_OR_PATTERN = /(chip\s*bag.*\bor\b.*candy\s*wrapper|candy\s*wrapper.*\bor\b.*chip\s*bag)/i;
const SIZING_PATTERN = /(chip\s*bag|candy\s*wrapper)/i;

const deriveOrderType = (items) => {
  const hasPrinted = items.some((i) => i.priceType === 'printed');
  const hasDigital = items.some((i) => i.priceType === 'digital');
  if (hasPrinted && hasDigital) return 'mixed';
  return hasPrinted ? 'printed' : 'digital';
};

const ORDER_TYPE_LABELS = {
  mixed: 'Mixed Order (Digital + Printed)',
  printed: 'Printed Order',
  digital: 'Digital Order',
};

const DesignDetailsForm = ({ cartItems, onRemoveFromCart, onUpdateQuantity }) => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);

  const set = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Load the last order + any saved draft from the backend
  useEffect(() => {
    const lastOrder = sessionStorage.getItem('lastOrder');
    if (!lastOrder) return;

    const order = JSON.parse(lastOrder);
    setOrderData(order);
    const baseUpdates = {
      orderNumber: order.orderNumber,
      fullName: order.customerInfo?.name || order.name || '',
      email: order.customerInfo?.email || order.email || '',
    };

    (async () => {
      try {
        const res = await getDesignDraft(order.orderNumber);
        if (res?.draft) {
          setFormData((prev) => ({ ...prev, ...res.draft, ...baseUpdates }));
          toast.info('Draft restored from previous session');
          return;
        }
      } catch (err) {
        console.error('Failed to load design draft:', err);
      }
      setFormData((prev) => ({ ...prev, ...baseUpdates }));
    })();
  }, []);

  // Auto-save draft to backend (debounced)
  useEffect(() => {
    if (!formData.orderNumber) return;
    const timeoutId = setTimeout(() => {
      saveDesignDraft(formData).catch((err) => {
        console.warn('Auto-save failed (best-effort):', err);
      });
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData]);

  // Derive order type & sizing flags from purchased cart items
  const items = orderData?.items || [];
  const hasPrintedItems = items.some((i) => i.priceType === 'printed');
  const derivedOrderType = deriveOrderType(items);
  const orderTypeLabel = ORDER_TYPE_LABELS[derivedOrderType];
  const needsFavorChoice = items.some((i) => (i.includes || []).some((line) => FAVOR_OR_PATTERN.test(line)));
  const showSizingInfo = items.some((i) => (i.includes || []).some((line) => SIZING_PATTERN.test(line)));

  // Keep formData.orderType in sync with derived value (so backend gets correct value)
  useEffect(() => {
    if (!orderData) return;
    const backendType = derivedOrderType === 'mixed' ? 'printed' : derivedOrderType;
    setFormData((prev) => (prev.orderType === backendType ? prev : { ...prev, orderType: backendType }));
  }, [orderData, derivedOrderType]);

  // Load existing uploads when orderNumber is known
  useEffect(() => {
    if (!formData.orderNumber) return;
    listUploads(formData.orderNumber)
      .then((res) => setUploads(res.files || []))
      .catch((err) => console.error('Failed to list uploads:', err));
  }, [formData.orderNumber]);

  const handleFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!formData.orderNumber) {
      toast.error('Order not loaded yet — please wait a moment.');
      return;
    }
    if (uploads.length + files.length > 5) {
      toast.error(`You can upload up to 5 inspiration images (already have ${uploads.length}).`);
      return;
    }
    setUploading(true);
    try {
      const newUploads = [];
      for (const f of files) {
        if (f.size > 8 * 1024 * 1024) {
          toast.error(`${f.name} is larger than 8 MB`);
          continue;
        }
        try {
          const res = await uploadInspirationImage(f, formData.orderNumber);
          newUploads.push(res);
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Upload failed for ${f.name}`);
        }
      }
      if (newUploads.length) {
        setUploads((prev) => [...prev, ...newUploads]);
        setFormData((prev) => ({
          ...prev,
          inspirationFileIds: [...(prev.inspirationFileIds || []), ...newUploads.map((u) => u.fileId)],
        }));
        toast.success(`${newUploads.length} image(s) uploaded`);
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveUpload = async (fileId) => {
    try {
      await deleteUpload(fileId);
      setUploads((prev) => prev.filter((u) => u.fileId !== fileId));
      setFormData((prev) => ({
        ...prev,
        inspirationFileIds: (prev.inspirationFileIds || []).filter((id) => id !== fileId),
      }));
    } catch (err) {
      console.error('Failed to remove upload:', err);
      toast.error('Failed to remove image');
    }
  };

  const validateForm = () => {
    if (!formData.agreedToTerms) {
      toast.error('Please agree to the terms before submitting');
      return false;
    }
    if (needsFavorChoice && !formData.favorItemChoice) {
      toast.error('Please select one included favor item (Chip Bag or Candy Wrapper)');
      return false;
    }
    if (hasPrintedItems && !formData.agreedToPricingAdjustment) {
      toast.error('Please acknowledge the pricing adjustment terms for printed items');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await submitDesignDetails(formData);
      toast.success('Thank you! Your design details have been received. You can expect your first proof within 3–5 business days. Please watch your email for updates.');
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit design details.');
    }
  };

  const handleItemToggle = (item) => {
    setFormData((prev) => ({
      ...prev,
      bundleItems: prev.bundleItems.includes(item)
        ? prev.bundleItems.filter((i) => i !== item)
        : [...prev.bundleItems, item],
    }));
  };

  return (
    <div className="min-h-screen bg-warm-ivory">
      <Header cartItems={cartItems} onRemoveFromCart={onRemoveFromCart} onUpdateQuantity={onUpdateQuantity} />

      <section className="pt-40 md:pt-44 pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <Card className="border-stone-taupe/20">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl text-charcoal-ink mb-2">Design Details Form</CardTitle>
              <CardDescription className="text-lg text-stone-taupe">
                Thank you for your purchase with The Favor!<br />
                This form collects the details needed to begin your custom design.<br />
                Design time begins once this form is fully submitted.
                <br /><br />
                <strong className="text-charcoal-ink">Please provide all information carefully. Changes requested after submission may count toward revision limits.</strong>
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8 mt-6">
                <OrderInfoSection formData={formData} set={set} />
                <EventDetailsSection formData={formData} set={set} orderTypeLabel={orderTypeLabel} />
                <WordingSection formData={formData} set={set} />
                <StyleSection formData={formData} set={set} />

                <BundleItemsSection
                  formData={formData}
                  set={set}
                  orderData={orderData}
                  showSizingInfo={showSizingInfo}
                  needsFavorChoice={needsFavorChoice}
                  onItemToggle={handleItemToggle}
                >
                  <InspirationUploads
                    uploads={uploads}
                    uploading={uploading}
                    onFilesPicked={handleFilesPicked}
                    onRemove={handleRemoveUpload}
                  />
                </BundleItemsSection>

                {hasPrintedItems && <PrintDetailsSection formData={formData} set={set} />}

                <CopyrightSection formData={formData} set={set} />
                <FinalNotesSection formData={formData} set={set} />

                <div className="flex gap-4 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="flex-1 border-stone-taupe/20 text-charcoal-ink hover:bg-soft-linen"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="design-details-submit-button"
                    className="flex-1 bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory"
                  >
                    Submit Design Details
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default DesignDetailsForm;
